import type { Question } from '@trivia/shared';

/** Space, and the distances that break human intuition. */
export const spaceQuestions: Question[] = [
  {
    id: 'space.milky-way-stars',
    type: 'numeric',
    prompt: 'Approximately how many stars are in the Milky Way?',
    category: 'Space',
    difficulty: 3,
    tags: ['stars'],
    answer: 2e11,
    displayAnswer: 'roughly 200 billion',
    unit: 'stars',
    magnitude: 'billions',
    tolerance: 0.4,
    reveal: {
      headline: 'roughly 200 billion',
      explanation:
        'Published estimates run from about 100 to 400 billion. Nobody counts them: the figure comes from the galaxy’s total mass and the observed distribution of star sizes.',
      comparison: 'If you named one star every second, you would finish in about 6,000 years.',
    },
    source: {
      citation: 'ESA Gaia mission / standard astronomical estimates',
      publisher: 'European Space Agency',
      kind: 'estimated',
      assumptions: ['Most of the galaxy is obscured by dust, so the total is inferred, not counted.'],
    },
  },
  {
    id: 'space.earths-to-moon',
    type: 'numeric',
    prompt: 'How many Earths could you line up side by side in the gap between Earth and the Moon?',
    category: 'Space',
    difficulty: 2,
    tags: ['moon', 'distance'],
    answer: 30,
    displayAnswer: 'about 30',
    unit: 'Earths',
    magnitude: 'small',
    tolerance: 0.22,
    reveal: {
      headline: 'about 30',
      explanation:
        'The Moon averages 384,400 km away and Earth is 12,742 km across, so a neat thirty Earths would fit in the gap. Almost every diagram of the Earth-Moon system draws it far too close.',
      comparison: 'At the right scale, every other planet in the Solar System would fit in that gap too.',
    },
    source: {
      citation: 'NASA planetary fact sheets',
      publisher: 'NASA',
      kind: 'measured',
    },
  },
  {
    id: 'space.sun-mass-share',
    type: 'percentage',
    prompt: 'What percentage of the Solar System’s total mass is the Sun?',
    category: 'Space',
    difficulty: 3,
    tags: ['sun'],
    answer: 99.86,
    displayAnswer: '99.86%',
    tolerance: 4,
    reveal: {
      headline: '99.86%',
      explanation:
        'The Sun holds essentially all of the mass. Jupiter is most of what is left over, and every other planet, moon, asteroid and comet combined amounts to a rounding error.',
      comparison: 'The Solar System is the Sun, plus some debris that failed to fall in.',
    },
    source: {
      citation: 'NASA Sun fact sheet',
      publisher: 'NASA',
      kind: 'measured',
    },
  },
  {
    id: 'space.iss-orbits',
    type: 'numeric',
    prompt: 'How many times does the International Space Station orbit Earth in a single day?',
    category: 'Space',
    difficulty: 2,
    tags: ['iss'],
    answer: 16,
    displayAnswer: 'about 16',
    unit: 'orbits',
    magnitude: 'small',
    tolerance: 0.16,
    reveal: {
      headline: 'about 16',
      explanation:
        'The station circles Earth roughly every 90 minutes at about 28,000 km/h, some 400 km up.',
      comparison: 'The crew sees sixteen sunrises and sixteen sunsets every day.',
    },
    source: {
      citation: 'NASA International Space Station facts',
      publisher: 'NASA',
      kind: 'measured',
    },
  },
  {
    id: 'space.galaxies',
    type: 'numeric',
    prompt: 'Approximately how many galaxies are there in the observable universe?',
    category: 'Space',
    difficulty: 4,
    tags: ['cosmology'],
    answer: 2e12,
    displayAnswer: 'about 2 trillion',
    unit: 'galaxies',
    magnitude: 'astronomical',
    tolerance: 0.6,
    reveal: {
      headline: 'about 2 trillion',
      explanation:
        'A 2016 analysis of deep-field images raised the estimate to roughly two trillion, most of them small and too faint to see. Later work using New Horizons data argues the true figure may be closer to a few hundred billion.',
      comparison: 'Even the low estimate means there are more galaxies than there are people who have ever lived — by a factor of a thousand.',
    },
    source: {
      citation: 'Conselice et al., "The evolution of galaxy number density"',
      publisher: 'The Astrophysical Journal',
      year: 2016,
      kind: 'estimated',
      assumptions: ['Estimates disagree by roughly an order of magnitude; this is the widely quoted figure.'],
    },
  },
  {
    id: 'space.voyager-distance',
    type: 'numeric',
    prompt: 'Voyager 1 launched in 1977 and has never stopped. Roughly how far from Earth is it now?',
    category: 'Space',
    difficulty: 3,
    tags: ['probes', 'distance'],
    answer: 2.5e10,
    displayAnswer: 'about 25 billion km',
    unit: 'km',
    magnitude: 'billions',
    tolerance: 0.35,
    reveal: {
      headline: 'about 25 billion km',
      explanation:
        'Voyager 1 is the most distant human-made object, travelling at around 17 km per second in interstellar space. Its radio signal takes over 22 hours to reach us.',
      comparison: 'After nearly fifty years at 61,000 km/h, it has covered less than a thousandth of the distance to the nearest star.',
    },
    source: {
      citation: 'NASA Voyager mission status',
      publisher: 'NASA',
      statisticYear: 2024,
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'space.people-in-space',
    type: 'numeric',
    prompt: 'How many people have ever been to space?',
    subPrompt: 'Anyone who has crossed 80 km altitude counts.',
    category: 'Space',
    difficulty: 3,
    tags: ['spaceflight'],
    answer: 700,
    displayAnswer: 'about 700',
    unit: 'people',
    magnitude: 'small',
    tolerance: 0.28,
    reveal: {
      headline: 'about 700',
      explanation:
        'Roughly 700 people have crossed into space since 1961 — fewer than fill a small theatre. Only 24 have travelled beyond low Earth orbit, and all of them went to the Moon between 1968 and 1972.',
      comparison: 'More people summit Everest in a single good season than have ever left the atmosphere.',
    },
    source: {
      citation: 'Astronaut and cosmonaut flight records',
      statisticYear: 2024,
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'space.trees-vs-stars',
    type: 'which-is-bigger',
    prompt: 'Which is there more of?',
    category: 'Space',
    difficulty: 2,
    tags: ['stars', 'forests'],
    options: [
      { label: 'Trees on Earth', detail: 'about 3 trillion' },
      { label: 'Stars in the Milky Way', detail: 'about 200 billion' },
    ],
    answer: 0,
    reveal: {
      headline: 'Trees',
      explanation:
        'There are roughly 3 trillion trees on Earth against a few hundred billion stars in our galaxy — about fifteen trees for every star in the Milky Way.',
      comparison: 'One planet out-counts an entire galaxy, using nothing but wood.',
    },
    source: {
      citation: 'Crowther et al., Nature (2015); ESA Gaia estimates',
      kind: 'estimated',
    },
  },
];
